import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Nav, Tab, Badge, Button, Spinner, Alert, Modal, Form, Table, ListGroup } from 'react-bootstrap';
import { FaUser, FaHeart, FaPills, FaFileAlt, FaCalendarAlt, FaHistory, FaShieldAlt, FaEdit, FaPlus, FaPhone, FaEnvelope, FaIdCard, FaDownload, FaEye, FaArrowLeft, FaUserMd, FaHome, FaExchangeAlt, FaMapMarkerAlt, FaSave, FaTimes, FaClipboardList, FaUserPlus, FaTrash, FaChartLine } from 'react-icons/fa';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import Select from 'react-select';

import patientRecordsService from '../../services/patientRecordsService';
import PatientFormsHub from './PatientFormsHub';
import PatientMedicationTab from './PatientMedicationTab';
import config from '../../config/config';
import '../componentCss/patientRecords.css';
import '../componentCss/patientDetailView.css';
import userService from '../../services/userService';
import { getCurrentUser, userHasRole } from '../../utils/authUtils';

// Modal Components
const DischargeModal = ({ show, onHide, onSubmit, patient, loading }) => {
    const [formData, setFormData] = useState({
        discharge_date: new Date().toISOString().split('T')[0],
        discharge_summary: '',
        discharge_instructions: '',
        discharge_medications: '',
        follow_up_required: false,
        follow_up_date: '',
        discharge_disposition: 'home'
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit({
            ...formData,
            discharge_medications: formData.discharge_medications.split('\n').filter(med => med.trim())
        });
    };

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    <FaHome className="me-2" />
                    Discharge Patient: {patient?.full_name}
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Discharge Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={formData.discharge_date}
                                    onChange={(e) => setFormData({ ...formData, discharge_date: e.target.value })}
                                    required
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Discharge Disposition</Form.Label>
                                <Form.Select
                                    value={formData.discharge_disposition}
                                    onChange={(e) => setFormData({ ...formData, discharge_disposition: e.target.value })}
                                >
                                    <option value="home">Home</option>
                                    <option value="another_facility">Another Facility</option>
                                    <option value="rehabilitation">Rehabilitation Center</option>
                                    <option value="nursing_home">Nursing Home</option>
                                    <option value="other">Other</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mb-3">
                        <Form.Label>Discharge Summary</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={formData.discharge_summary}
                            onChange={(e) => setFormData({ ...formData, discharge_summary: e.target.value })}
                            placeholder="Patient condition, treatment provided, response to treatment..."
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Discharge Instructions</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={formData.discharge_instructions}
                            onChange={(e) => setFormData({ ...formData, discharge_instructions: e.target.value })}
                            placeholder="Instructions for patient care at home..."
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Discharge Medications (one per line)</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={formData.discharge_medications}
                            onChange={(e) => setFormData({ ...formData, discharge_medications: e.target.value })}
                            placeholder="Medication 1 - dosage and instructions&#10;Medication 2 - dosage and instructions"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            label="Follow-up appointment required"
                            checked={formData.follow_up_required}
                            onChange={(e) => setFormData({ ...formData, follow_up_required: e.target.checked })}
                        />
                    </Form.Group>

                    {formData.follow_up_required && (
                        <Form.Group className="mb-3">
                            <Form.Label>Follow-up Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={formData.follow_up_date}
                                onChange={(e) => setFormData({ ...formData, follow_up_date: e.target.value })}
                            />
                        </Form.Group>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={loading}>
                        Cancel
                    </Button>
                    <Button variant="success" type="submit" disabled={loading}>
                        {loading ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Discharging...
                            </>
                        ) : (
                            <>
                                <FaHome className="me-2" />
                                Discharge Patient
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

const TransferModal = ({ show, onHide, onSubmit, patient, loading }) => {
    const [formData, setFormData] = useState({
        transfer_date: new Date().toISOString().split('T')[0],
        transfer_from: '',
        transfer_to: '',
        transfer_reason: '',
        transfer_notes: '',
        accepting_physician: '',
        transfer_mode: 'internal'
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);
    };

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    <FaExchangeAlt className="me-2" />
                    Transfer Patient: {patient?.full_name}
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Transfer Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={formData.transfer_date}
                                    onChange={(e) => setFormData({ ...formData, transfer_date: e.target.value })}
                                    required
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Transfer Mode</Form.Label>
                                <Form.Select
                                    value={formData.transfer_mode}
                                    onChange={(e) => setFormData({ ...formData, transfer_mode: e.target.value })}
                                >
                                    <option value="internal">Internal Transfer</option>
                                    <option value="external">External Transfer</option>
                                    <option value="emergency">Emergency Transfer</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Transfer From</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.transfer_from}
                                    onChange={(e) => setFormData({ ...formData, transfer_from: e.target.value })}
                                    placeholder="Current department/unit"
                                    required
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Transfer To</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.transfer_to}
                                    onChange={(e) => setFormData({ ...formData, transfer_to: e.target.value })}
                                    placeholder="Destination department/facility"
                                    required
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mb-3">
                        <Form.Label>Accepting Physician</Form.Label>
                        <Form.Control
                            type="text"
                            value={formData.accepting_physician}
                            onChange={(e) => setFormData({ ...formData, accepting_physician: e.target.value })}
                            placeholder="Name of accepting physician"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Transfer Reason</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={formData.transfer_reason}
                            onChange={(e) => setFormData({ ...formData, transfer_reason: e.target.value })}
                            placeholder="Reason for transfer..."
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Transfer Notes</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={formData.transfer_notes}
                            onChange={(e) => setFormData({ ...formData, transfer_notes: e.target.value })}
                            placeholder="Additional notes, special instructions..."
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={loading}>
                        Cancel
                    </Button>
                    <Button variant="info" type="submit" disabled={loading}>
                        {loading ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Transferring...
                            </>
                        ) : (
                            <>
                                <FaExchangeAlt className="me-2" />
                                Transfer Patient
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

const LocationModal = ({ show, onHide, onSubmit, patient, loading }) => {
    const [formData, setFormData] = useState({
        department: '',
        ward: '',
        room: '',
        bed: '',
        floor: '',
        unit: ''
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);
    };

    return (
        <Modal show={show} onHide={onHide}>
            <Modal.Header closeButton>
                <Modal.Title>
                    <FaMapMarkerAlt className="me-2" />
                    Update Location: {patient?.full_name}
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Department</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.department}
                                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                                    placeholder="e.g., Emergency, ICU, General Ward"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Ward</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.ward}
                                    onChange={(e) => setFormData({ ...formData, ward: e.target.value })}
                                    placeholder="e.g., Ward A, Medical Ward"
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Floor</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.floor}
                                    onChange={(e) => setFormData({ ...formData, floor: e.target.value })}
                                    placeholder="e.g., 2nd Floor"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Room</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.room}
                                    onChange={(e) => setFormData({ ...formData, room: e.target.value })}
                                    placeholder="e.g., 201, 305A"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Bed</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.bed}
                                    onChange={(e) => setFormData({ ...formData, bed: e.target.value })}
                                    placeholder="e.g., Bed 1, Bed A"
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mb-3">
                        <Form.Label>Unit</Form.Label>
                        <Form.Control
                            type="text"
                            value={formData.unit}
                            onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                            placeholder="e.g., Cardiac Unit, Surgical Unit"
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={loading}>
                        Cancel
                    </Button>
                    <Button variant="warning" type="submit" disabled={loading}>
                        {loading ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Updating...
                            </>
                        ) : (
                            <>
                                <FaMapMarkerAlt className="me-2" />
                                Update Location
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

const EditPatientModal = ({ show, onHide, onSubmit, patient, loading }) => {
    const [formData, setFormData] = useState({
        // Personal Information - EMR Required
        first_name: '',
        last_name: '',
        middle_name: '',
        date_of_birth: '',
        gender: '',
        religion: '',
        race: '',
        language: '',
        marital_status: '',
        nationality: '',
        country: '',
        state: '',
        citizenship: '',
        city: '',
        
        // Contact Information - EMR Required
        phone_primary: '',
        permanent_address: '',
        
        // Medical Information - EMR Required
        allergy: '',
        primary_physician: '',
        primary_physician_license: '',
        
        // Identification - EMR Required
        emirates_id: '',
        
        // Optional Fields
        phone_secondary: '',
        email: '',
        passport_number: '',
        insurance_provider: '',
        insurance_policy_number: '',
        insurance_subscriber_name: '',
        
        // Emergency Contact - Optional
        emergency_contact_name: '',
        emergency_contact_phone: '',
        emergency_contact_relationship: '',
        
        // Additional Medical fields
        blood_group: '',
        height: '',
        weight: ''
    });
    
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (patient && show) {
            console.log('🔍 EditPatientModal - Patient data received:', patient);
            
            // Parse phone number to separate country code and number
            const parsePhoneNumber = (fullPhone) => {
                if (!fullPhone) return { countryCode: '+971', phoneNumber: '' };
                
                const phone = fullPhone.toString();
                const countryCode = phone.startsWith('+971') ? '+971' : 
                                   phone.startsWith('+1') ? '+1' : 
                                   phone.startsWith('+44') ? '+44' : '+971';
                
                if (countryCode === '+971') {
                    return {
                        countryCode: '+971',
                        phoneNumber: phone.startsWith('+971') ? phone.substring(4) : phone
                    };
                }
                
                return {
                    countryCode: countryCode,
                    phoneNumber: phone.substring(countryCode.length)
                };
            };

            const primaryPhone = parsePhoneNumber(patient.phone || patient.phone_primary);
            const emergencyPhone = parsePhoneNumber(
                patient.emergency_contacts?.[0]?.phone || 
                patient.emergency_contact_phone
            );

            // Map data from flattened structure (formatPatientForDisplay output)
            // Also check for raw demographics data if available
            const mappedData = {
                // Personal Information - EMR Required
                first_name: patient.first_name || patient.demographics?.first_name || '',
                last_name: patient.last_name || patient.demographics?.last_name || '',
                middle_name: patient.middle_name || patient.demographics?.middle_name || '',
                date_of_birth: patient.date_of_birth
                    ? patient.date_of_birth.split('T')[0]
                    : patient.demographics?.date_of_birth
                        ? patient.demographics.date_of_birth.split('T')[0]
                        : '',
                gender: patient.gender || patient.demographics?.gender || '',
                religion: patient.religion || patient.demographics?.religion || '',
                race: patient.race || patient.demographics?.race || '',
                language: patient.language || patient.demographics?.language || '',
                marital_status: patient.marital_status || patient.demographics?.marital_status || '',
                nationality: patient.nationality || patient.demographics?.nationality || '',
                country: patient.address?.country || patient.country || patient.demographics?.country || 'UAE',
                state: patient.address?.state || patient.state || patient.demographics?.state || '',
                citizenship: patient.citizenship || patient.demographics?.citizenship || '',
                city: patient.address?.city || patient.city || patient.demographics?.city || '',
                
                // Contact Information - EMR Required
                phone_primary: primaryPhone.countryCode + primaryPhone.phoneNumber,
                permanent_address: patient.address?.street || patient.permanent_address || patient.demographics?.permanent_address || '',
                
                // Medical Information - EMR Required
                allergy: patient.allergies?.join(', ') || patient.allergy || patient.demographics?.allergy || '',
                primary_physician: patient.attending_physician || patient.primary_physician || patient.demographics?.primary_physician || '',
                primary_physician_license: patient.primary_physician_license || patient.demographics?.primary_physician_license || '',
                
                // Identification - EMR Required
                emirates_id: patient.emirates_id || patient.demographics?.emirates_id || '',
                
                // Optional Fields
                phone_secondary: patient.phone_secondary || patient.demographics?.phone_secondary || '',
                email: patient.email || patient.demographics?.email || '',
                passport_number: patient.passport_number || patient.demographics?.passport_number || '',
                insurance_provider: patient.insurance_info?.[0]?.provider || patient.insurance_provider || patient.demographics?.insurance_provider || '',
                insurance_policy_number: patient.insurance_info?.[0]?.policy_number || patient.insurance_policy_number || patient.demographics?.insurance_policy_number || '',
                insurance_subscriber_name: patient.insurance_info?.[0]?.subscriber_name || patient.insurance_subscriber_name || patient.demographics?.insurance_subscriber_name || '',
                
                // Emergency Contact - Optional
                emergency_contact_name: patient.emergency_contacts?.[0]?.name || patient.emergency_contact_name || patient.demographics?.emergency_contacts?.[0]?.name || '',
                emergency_contact_phone: emergencyPhone.countryCode + emergencyPhone.phoneNumber,
                emergency_contact_relationship: patient.emergency_contacts?.[0]?.relationship || patient.emergency_contact_relationship || patient.demographics?.emergency_contacts?.[0]?.relationship || '',
                
                // Additional Medical fields
                blood_group: patient.blood_group || patient.medical_record?.blood_group || 'unknown',
                height: patient.height || patient.medical_record?.height || '',
                weight: patient.weight || patient.medical_record?.weight || ''
            };

            console.log('🔍 EditPatientModal - Mapped form data:', mappedData);
            setFormData(mappedData);
            setErrors({}); // Clear errors when modal opens
        }
    }, [patient, show]);

    const validateForm = () => {
        const newErrors = {};
        
        // Personal Information - EMR Required
        if (!formData.first_name?.trim()) {
            newErrors.first_name = 'First name is required';
        }
        if (!formData.last_name?.trim()) {
            newErrors.last_name = 'Last name is required';
        }
        if (!formData.date_of_birth) {
            newErrors.date_of_birth = 'Date of birth is required';
        }
        if (!formData.gender) {
            newErrors.gender = 'Gender is required';
        }
        if (!formData.religion?.trim()) {
            newErrors.religion = 'Religion is required';
        }
        if (!formData.race?.trim()) {
            newErrors.race = 'Race is required';
        }
        if (!formData.language?.trim()) {
            newErrors.language = 'Language is required';
        }
        if (!formData.marital_status) {
            newErrors.marital_status = 'Marital status is required';
        }
        if (!formData.nationality?.trim()) {
            newErrors.nationality = 'Nationality is required';
        }
        if (!formData.country) {
            newErrors.country = 'Country is required';
        }
        if (!formData.state?.trim()) {
            newErrors.state = 'State is required';
        }
        if (!formData.citizenship) {
            newErrors.citizenship = 'Citizenship is required';
        }
        if (!formData.city?.trim()) {
            newErrors.city = 'City is required';
        }
        
        // Contact Information - EMR Required
        if (!formData.phone_primary) {
            newErrors.phone_primary = 'Phone number is required';
        } else if (!/^\+?[1-9]\d{1,14}$/.test(formData.phone_primary)) {
            newErrors.phone_primary = 'Please enter a valid phone number with country code';
        }
        if (!formData.permanent_address?.trim()) {
            newErrors.permanent_address = 'Permanent address is required';
        }
        
        // Medical Information - EMR Required
        if (!formData.allergy?.trim()) {
            newErrors.allergy = 'Allergy information is required';
        }
        if (!formData.primary_physician?.trim()) {
            newErrors.primary_physician = 'Primary physician is required';
        }
        if (!formData.primary_physician_license?.trim()) {
            newErrors.primary_physician_license = 'Primary physician license is required';
        }
        
        // Identification - Emirates ID Optional
        if (formData.emirates_id?.trim() && !/^784\d{12}$/.test(formData.emirates_id)) {
            newErrors.emirates_id = 'Emirates ID must be 15 digits starting with 784';
        }
        
        // Optional field validations
        if (formData.phone_secondary && !/^\+?[1-9]\d{1,14}$/.test(formData.phone_secondary)) {
            newErrors.phone_secondary = 'Please enter a valid phone number with country code';
        }
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        if (formData.emergency_contact_phone && !/^\+?[1-9]\d{1,14}$/.test(formData.emergency_contact_phone)) {
            newErrors.emergency_contact_phone = 'Please enter a valid phone number with country code';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            toast.error('Please correct the validation errors');
            return;
        }
        
        onSubmit({
            demographics: formData
        });
    };

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    <FaEdit className="me-2" />
                    Edit Patient: {patient?.full_name}
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <Row>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>First Name <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.first_name}
                                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                                    isInvalid={!!errors.first_name}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.first_name}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Middle Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.middle_name}
                                    onChange={(e) => setFormData({ ...formData, middle_name: e.target.value })}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Last Name <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.last_name}
                                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                    isInvalid={!!errors.last_name}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.last_name}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Date of Birth <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="date"
                                    value={formData.date_of_birth}
                                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                                    isInvalid={!!errors.date_of_birth}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.date_of_birth}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Gender <span className="text-danger">*</span></Form.Label>
                                <Form.Select
                                    value={formData.gender}
                                    onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                                    isInvalid={!!errors.gender}
                                    required
                                >
                                    <option value="">Select Gender</option>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                    <option value="other">Other</option>
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {errors.gender}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Religion <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.religion}
                                    onChange={(e) => setFormData({ ...formData, religion: e.target.value })}
                                    isInvalid={!!errors.religion}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.religion}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Race <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.race}
                                    onChange={(e) => setFormData({ ...formData, race: e.target.value })}
                                    isInvalid={!!errors.race}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.race}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Language <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.language}
                                    onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                                    isInvalid={!!errors.language}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.language}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Marital Status <span className="text-danger">*</span></Form.Label>
                                <Form.Select
                                    value={formData.marital_status}
                                    onChange={(e) => setFormData({ ...formData, marital_status: e.target.value })}
                                    isInvalid={!!errors.marital_status}
                                    required
                                >
                                    <option value="">Select Marital Status</option>
                                    <option value="single">Single</option>
                                    <option value="married">Married</option>
                                    <option value="divorced">Divorced</option>
                                    <option value="widowed">Widowed</option>
                                    <option value="separated">Separated</option>
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {errors.marital_status}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Nationality <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.nationality}
                                    onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                                    isInvalid={!!errors.nationality}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.nationality}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Country <span className="text-danger">*</span></Form.Label>
                                <Form.Select
                                    value={formData.country}
                                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                                    isInvalid={!!errors.country}
                                    required
                                >
                                    <option value="">Select Country</option>
                                    <option value="UAE">United Arab Emirates</option>
                                    <option value="USA">United States</option>
                                    <option value="UK">United Kingdom</option>
                                    <option value="Canada">Canada</option>
                                    <option value="Australia">Australia</option>
                                    <option value="Other">Other</option>
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {errors.country}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>State <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.state}
                                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                                    isInvalid={!!errors.state}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.state}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Citizenship <span className="text-danger">*</span></Form.Label>
                                <Form.Select
                                    value={formData.citizenship}
                                    onChange={(e) => setFormData({ ...formData, citizenship: e.target.value })}
                                    isInvalid={!!errors.citizenship}
                                    required
                                >
                                    <option value="">Select Citizenship</option>
                                    <option value="UAE">UAE Citizen</option>
                                    <option value="Resident">UAE Resident</option>
                                    <option value="Visitor">Visitor</option>
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {errors.citizenship}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>City <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.city}
                                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                                    isInvalid={!!errors.city}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.city}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Emirates ID <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.emirates_id}
                                    onChange={(e) => setFormData({ ...formData, emirates_id: e.target.value })}
                                    isInvalid={!!errors.emirates_id}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.emirates_id}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Primary Phone <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="tel"
                                    value={formData.phone_primary}
                                    onChange={(e) => setFormData({ ...formData, phone_primary: e.target.value })}
                                    isInvalid={!!errors.phone_primary}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.phone_primary}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Secondary Phone</Form.Label>
                                <Form.Control
                                    type="tel"
                                    value={formData.phone_secondary}
                                    onChange={(e) => setFormData({ ...formData, phone_secondary: e.target.value })}
                                    isInvalid={!!errors.phone_secondary}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.phone_secondary}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>Permanent Address <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={formData.permanent_address}
                                    onChange={(e) => setFormData({ ...formData, permanent_address: e.target.value })}
                                    isInvalid={!!errors.permanent_address}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.permanent_address}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Allergy <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.allergy}
                                    onChange={(e) => setFormData({ ...formData, allergy: e.target.value })}
                                    isInvalid={!!errors.allergy}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.allergy}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Primary Physician <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.primary_physician}
                                    onChange={(e) => setFormData({ ...formData, primary_physician: e.target.value })}
                                    isInvalid={!!errors.primary_physician}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.primary_physician}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Physician License <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.primary_physician_license}
                                    onChange={(e) => setFormData({ ...formData, primary_physician_license: e.target.value })}
                                    isInvalid={!!errors.primary_physician_license}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.primary_physician_license}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={loading}>
                        Cancel
                    </Button>
                    <Button variant="primary" type="submit" disabled={loading}>
                        {loading ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <FaSave className="me-2" />
                                Save Changes
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

// Staff Assignment Modal Component
const StaffAssignmentModal = ({ show, onHide, onSubmit, patient, loading }) => {
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [formData, setFormData] = useState({
        user_id: '',
        assignment_type: 'primary'
    });

    useEffect(() => {
        if (show) {
            loadUsers();
            setSelectedUser(null); // Reset selection when modal opens
        }
    }, [show]);

    const loadUsers = async () => {
        try {
            setLoadingUsers(true);
            // Use the new cached method with increased limit
            const response = await userService.getUsersForAssignment({
                page: 1,
                page_size: 2000,
                is_active: true,
                timeout: 300000
            });
            console.log('Users response (cached):', response);
            if (response.success) {
                // Users are in response.data.items
                const usersList = response.data?.items || response.data?.users || response.users || [];
                console.log('Users list:', usersList.length, 'users loaded');
                setUsers(usersList);
            } else {
                toast.error('Failed to load users');
            }
        } catch (error) {
            console.error('Error loading users:', error);
            toast.error('Failed to load users');
        } finally {
            setLoadingUsers(false);
        }
    };

    // Transform users to react-select format
    const userOptions = (users || []).map(user => {
        const displayName = user.full_name ||
                           `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
                           user.username;
        const department = user.department ? ` • Dept: ${user.department}` : '';
        const specialty = user.specialty ? ` • ${user.specialty}` : '';

        return {
            value: user.id,
            label: `${displayName} (${user.email})${department}${specialty}`,
            user: user // Keep full user object for reference
        };
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.user_id) {
            toast.error('Please select a user');
            return;
        }
        onSubmit(formData);
    };

    const handleRemoveStaff = (staffUserId) => {
        if (window.confirm('Are you sure you want to remove this staff assignment?')) {
            // TODO: Implement remove staff functionality
            toast.info('Remove staff functionality coming soon');
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    <FaUserPlus className="me-2" />
                    Assign Staff to Patient: {patient?.full_name}
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {/* Current Staff Assignments */}
                    {patient?.care_status?.assigned_staff && patient.care_status.assigned_staff.length > 0 && (
                        <div className="mb-4">
                            <h6 className="mb-3">Current Staff Assignments</h6>
                            <ListGroup>
                                {patient.care_status.assigned_staff.map((staff, index) => (
                                    <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                        <div>
                            <strong>User ID:</strong> {staff.user_id}<br />
                            <strong>Assignment Type:</strong> <Badge bg="info">{staff.assignment_type || staff.role}</Badge><br />
                            <small className="text-muted">
                                Assigned: {staff.start_date ? new Date(staff.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'N/A'}
                            </small>
                        </div>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => handleRemoveStaff(staff.user_id)}
                                        >
                                            <FaTrash />
                                        </Button>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                            <hr />
                        </div>
                    )}

                    {/* Add New Staff Assignment */}
                    <h6 className="mb-3">Add New Staff Assignment</h6>

                    {loadingUsers ? (
                        <div className="text-center py-3">
                            <Spinner animation="border" size="sm" />
                            <p className="mt-2 text-muted">Loading users...</p>
                        </div>
                    ) : (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>Search and Select User/Staff <span className="text-danger">*</span></Form.Label>
                                <Select
                                    name="user_id"
                                    options={userOptions}
                                    value={selectedUser}
                                    onChange={(selectedOption) => {
                                        setSelectedUser(selectedOption);
                                        setFormData({
                                            ...formData,
                                            user_id: selectedOption?.value || ''
                                        });
                                    }}
                                    placeholder="Type to search by name, email, department, or specialty..."
                                    isClearable
                                    isSearchable
                                    className="basic-single"
                                    classNamePrefix="select"
                                    noOptionsMessage={() => "No users found"}
                                    isLoading={loadingUsers}
                                    isDisabled={loadingUsers}
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        control: base => ({
                                            ...base,
                                            minHeight: '38px',
                                            borderColor: '#ced4da',
                                            '&:hover': { borderColor: '#86b7fe' }
                                        })
                                    }}
                                />
                                <Form.Text className="text-muted">
                                    {users.length} users available • Start typing to filter
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Assignment Type <span className="text-danger">*</span></Form.Label>
                                <Form.Select
                                    value={formData.assignment_type}
                                    onChange={(e) => setFormData({ ...formData, assignment_type: e.target.value })}
                                    required
                                >
                                    <option value="primary">Primary</option>
                                    <option value="secondary">Secondary</option>
                                    <option value="consultant">Consultant</option>
                                </Form.Select>
                                <Form.Text className="text-muted">
                                    Use "Primary" for main caregivers (parents, primary staff)
                                </Form.Text>
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={loading}>
                        Cancel
                    </Button>
                    <Button variant="primary" type="submit" disabled={loading || loadingUsers}>
                        {loading ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Assigning...
                            </>
                        ) : (
                            <>
                                <FaUserPlus className="me-2" />
                                Assign Staff
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

const PatientDetailView = () => {
    const { patientId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // Role detection for view mode
    const currentUser = getCurrentUser();
    const isParentView = userHasRole('parent');
    const viewMode = isParentView ? 'view' : 'edit';

    // State Management
    const [patient, setPatient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('demographics');
    const [refreshing, setRefreshing] = useState(false);

    // Additional data states
    const [medicalHistory, setMedicalHistory] = useState({});
    const [medications, setMedications] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [appointments, setAppointments] = useState([]);

    // Modal states
    const [showAddMedication, setShowAddMedication] = useState(false);
    const [showUploadDocument, setShowUploadDocument] = useState(false);
    const [showDischargeModal, setShowDischargeModal] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showStaffAssignmentModal, setShowStaffAssignmentModal] = useState(false);

    // Action loading states
    const [dischargeLoading, setDischargeLoading] = useState(false);
    const [transferLoading, setTransferLoading] = useState(false);
    const [locationLoading, setLocationLoading] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [staffAssignmentLoading, setStaffAssignmentLoading] = useState(false);

    // Load patient data
    const loadPatientData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await patientRecordsService.getPatientDetails(patientId);

            if (response.success) {
                const formattedPatient = patientRecordsService.formatPatientForDisplay(response.patient);
                
                // Store both formatted and raw patient data
                setPatient({
                    ...formattedPatient,
                    // Include raw demographics data for edit modal
                    demographics: response.patient.demographics || {},
                    medical_record: response.patient.medical_record || {},
                    // Include other raw fields that might be needed
                    raw_data: response.patient
                });
                
                console.log('🔍 PatientDetailView - Loaded patient data:', {
                    formatted: formattedPatient,
                    raw: response.patient
                });
            } else {
                setError(response.error || 'Failed to load patient details');
                toast.error(response.error || 'Failed to load patient details');
            }
        } catch (err) {
            setError('An unexpected error occurred');
            toast.error('An unexpected error occurred');
        } finally {
            setLoading(false);
        }
    }, [patientId]);

    // Load additional data based on active tab
    const loadTabData = useCallback(async (tabKey) => {
        if (!patient) return;

        try {
            switch (tabKey) {
                case 'medical':
                    if (Object.keys(medicalHistory).length === 0) {
                        const response = await patientRecordsService.getPatientMedicalHistory(patientId);
                        if (response.success) {
                            // Store the medical history object directly
                            setMedicalHistory(response.medical_history);
                        }
                    }
                    break;
                case 'medications':
                    if (medications.length === 0) {
                        const response = await patientRecordsService.getPatientMedications(patientId);
                        if (response.success) {
                            setMedications(response.medications);
                        }
                    }
                    break;
                case 'documents':
                    if (documents.length === 0) {
                        const response = await patientRecordsService.getPatientDocuments(patientId);
                        if (response.success) {
                            setDocuments(response.documents);
                        }
                    }
                    break;
                case 'appointments':
                    if (appointments.length === 0) {
                        const response = await patientRecordsService.getPatientAppointments(patientId);
                        if (response.success) {
                            setAppointments(response.appointments);
                        }
                    }
                    break;
            }
        } catch (error) {
        }
    }, [patient, patientId, medications.length, documents.length, appointments.length]);

    // Initial load
    useEffect(() => {
        if (patientId) {
            loadPatientData();
        }
    }, [patientId, loadPatientData]);

    // Handle navigation state for direct form access
    useEffect(() => {
        if (location.state?.activeTab) {
            setActiveTab(location.state.activeTab);
        }
    }, [location.state]);

    // Load tab data when tab changes
    useEffect(() => {
        loadTabData(activeTab);
    }, [activeTab, loadTabData]);

    // Handle tab change
    const handleTabChange = useCallback((tabKey) => {
        setActiveTab(tabKey);
    }, []);

    // Refresh data
    const handleRefresh = useCallback(async () => {
        setRefreshing(true);
        await loadPatientData();
        // Clear tab data to force reload
        setMedicalHistory({});
        setMedications([]);
        setDocuments([]);
        setAppointments([]);
        setRefreshing(false);
    }, [loadPatientData]);

    // Patient Action Handlers
    const handleDischarge = useCallback(async (dischargeData) => {
        setDischargeLoading(true);
        try {
            const response = await patientRecordsService.dischargePatient(patient.id, dischargeData);
            if (response.success) {
                toast.success(response.message);
                setShowDischargeModal(false);
                await handleRefresh();
            } else {
                toast.error(response.error);
            }
        } catch (error) {
            toast.error('Failed to discharge patient');
        } finally {
            setDischargeLoading(false);
        }
    }, [patient?.id, handleRefresh]);

    const handleTransfer = useCallback(async (transferData) => {
        setTransferLoading(true);
        try {
            const response = await patientRecordsService.transferPatient(patient.id, transferData);
            if (response.success) {
                toast.success(response.message);
                setShowTransferModal(false);
                await handleRefresh();
            } else {
                toast.error(response.error);
            }
        } catch (error) {
            toast.error('Failed to transfer patient');
        } finally {
            setTransferLoading(false);
        }
    }, [patient?.id, handleRefresh]);

    const handleLocationUpdate = useCallback(async (locationData) => {
        setLocationLoading(true);
        try {
            const response = await patientRecordsService.updatePatientLocation(patient.id, locationData);
            if (response.success) {
                toast.success(response.message);
                setShowLocationModal(false);
                await handleRefresh();
            } else {
                toast.error(response.error);
            }
        } catch (error) {
            toast.error('Failed to update patient location');
        } finally {
            setLocationLoading(false);
        }
    }, [patient?.id, handleRefresh]);

    const handleEditPatient = useCallback(async (patientData) => {
        setEditLoading(true);
        try {
            console.log('Updating patient demographics:', patientData);
            
            // Use the modern patient service to update demographics
            const updatePayload = {
                demographics: patientData.demographics
            };
            
            const response = await patientRecordsService.updatePatient(patient.id, updatePayload);
            
            console.log('Update response:', response);
            
            if (response.success) {
                toast.success('Patient demographics updated successfully!');
                setShowEditModal(false);
                
                // Update the patient state immediately for better UX
                setPatient(prevPatient => ({
                    ...prevPatient,
                    ...patientData.demographics,
                    phone: patientData.demographics.phone_primary, // Map phone_primary to phone
                }));
                
                // Refresh the full patient data
                setTimeout(() => {
                    handleRefresh();
                }, 500);
            } else {
                toast.error(response.error || 'Failed to update patient details');
            }
        } catch (error) {
            console.error('Error updating patient:', error);
            toast.error(error.response?.data?.detail || 'Failed to update patient information');
        } finally {
            setEditLoading(false);
        }
    }, [patient?.id, handleRefresh]);

    const handleStaffAssignment = useCallback(async (staffData) => {
        setStaffAssignmentLoading(true);
        try {
            console.log('Assigning staff to patient:', staffData);
            
            const response = await patientRecordsService.assignStaffToPatient(patient.id, staffData);
            
            console.log('Staff assignment response:', response);
            
            if (response.success) {
                toast.success('Staff assigned successfully!');
                setShowStaffAssignmentModal(false);
                
                // Refresh the patient data to show updated staff assignments
                await handleRefresh();
            } else {
                toast.error(response.error || 'Failed to assign staff');
            }
        } catch (error) {
            console.error('Error assigning staff:', error);
            toast.error(error.response?.data?.detail || 'Failed to assign staff to patient');
        } finally {
            setStaffAssignmentLoading(false);
        }
    }, [patient?.id, handleRefresh]);

    // Handle medication order completion
    const handleMedicationOrdered = useCallback(async (orderData) => {
        try {
            toast.success('Medication order submitted successfully');
            // Optionally refresh patient data to show updated medication list
            await handleRefresh();
        } catch (error) {
            toast.error('Failed to process medication order');
            console.error('Medication order error:', error);
        }
    }, [handleRefresh]);

    // Patient Header Component
    const PatientHeader = ({ patient }) => {
        if (!patient) return null;

        const statusConfig = patientRecordsService.getStatusBadgeConfig(patient.status);

        return (
            <Card className="patient-header shadow-sm border-0 mb-4">
                <Card.Body className="p-4">
                    <Row className="align-items-center">
                        <Col md={8}>
                            <div className="d-flex align-items-center mb-3">
                                <div className="patient-avatar me-3">
                                    <div className="avatar-circle bg-primary text-white d-flex align-items-center justify-content-center">
                                        <FaUser size={24} />
                                    </div>
                                </div>
                                <div>
                                    <h3 className="patient-name mb-1">{patient.full_name}</h3>
                                    <p className="patient-mrn text-muted mb-1">
                                        <strong>MRN:</strong> {patient.mrn}
                                    </p>
                                    <Badge bg={statusConfig.color} className="status-badge">
                                        {statusConfig.icon} {statusConfig.label}
                                    </Badge>
                                </div>
                            </div>

                            <Row className="patient-basic-info">
                                <Col sm={6} md={3}>
                                    <small className="text-muted d-block">Age</small>
                                    <strong>{patient.age}</strong>
                                </Col>
                                <Col sm={6} md={3}>
                                    <small className="text-muted d-block">Gender</small>
                                    <strong>{patient.gender}</strong>
                                </Col>
                                <Col sm={6} md={3}>
                                    <small className="text-muted d-block">Registration</small>
                                    <strong>{patient.is_registered ? 'Completed' : 'Pending'}</strong>
                                </Col>
                                <Col sm={6} md={3}>
                                    <small className="text-muted d-block">Last Visit</small>
                                    <strong>
                                        {patient.last_visit
                                            ? new Date(patient.last_visit).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
                                            : 'Never'
                                        }
                                    </strong>
                                </Col>
                            </Row>

                            {patient.current_services && patient.current_services.length > 0 && (
                                <div className="patient-services mt-3">
                                    <small className="text-muted d-block mb-2">Current Services</small>
                                    <div>
                                        {patient.current_services.map((service, index) => (
                                            <Badge key={index} bg="light" text="dark" className="me-2 mb-1">
                                                {service}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </Col>

                        <Col md={4} className="text-md-end">
                            <div className="patient-actions">
                                <Row className="g-2">
                                    {/* <Col xs={12} sm={6} lg={4}>
                                        <Button
                                            variant="outline-primary"
                                            className="w-100"
                                            size="sm"
                                            onClick={() => setShowEditModal(true)}
                                        >
                                            <FaEdit className="me-1" />
                                            Edit Details
                                        </Button>
                                    </Col> */}
                                    {/* <Col xs={12} sm={6} lg={4}>
                                        <Button
                                            variant="outline-warning"
                                            className="w-100"
                                            size="sm"
                                            onClick={() => setShowLocationModal(true)}
                                        >
                                            <FaMapMarkerAlt className="me-1" />
                                            Update Location
                                        </Button>
                                    </Col>
                                    <Col xs={12} sm={6} lg={4}>
                                        <Button
                                            variant="outline-info"
                                            className="w-100"
                                            size="sm"
                                            onClick={() => setShowTransferModal(true)}
                                            disabled={patient.status === 'discharged'}
                                        >
                                            <FaExchangeAlt className="me-1" />
                                            Transfer
                                        </Button>
                                    </Col> */}
                                    {/* <Col xs={12} sm={6} lg={4}>
                                        <Button
                                            variant="outline-success"
                                            className="w-100"
                                            size="sm"
                                            onClick={() => setShowDischargeModal(true)}
                                            disabled={patient.status === 'discharged'}
                                        >
                                            <FaHome className="me-1" />
                                            Discharge
                                        </Button>
                                    </Col> */}
                                    {!isParentView && (!patient.is_registered || patient.registration_status === 'pre_registered') && (
                                        <Col xs={12} sm={6} lg={4}>
                                            <Button
                                                variant="outline-primary"
                                                className="w-100"
                                                size="sm"
                                                onClick={() => navigate(`/complete-registration/${patient.id}`)}
                                            >
                                                <FaUserMd className="me-1" />
                                                Complete Registration
                                            </Button>
                                        </Col>
                                    )}
                                    {!isParentView && (
                                        <Col xs={12} sm={6} lg={4}>
                                            <Button
                                                variant="outline-primary"
                                                className="w-100"
                                                size="sm"
                                                onClick={() => setShowStaffAssignmentModal(true)}
                                            >
                                                <FaUserPlus className="me-1" />
                                                Assign Staff
                                            </Button>
                                        </Col>
                                    )}
                                    <Col xs={12} sm={6} lg={4}>
                                        <Button
                                            variant="outline-danger"
                                            className="w-100"
                                            size="sm"
                                            onClick={() => navigate('/patient-records')}
                                        >
                                            <FaArrowLeft className="me-1" />
                                            Back
                                        </Button>
                                    </Col>
                                    <Col xs={12} sm={6} lg={4}>
                                        <Button
                                            variant="outline-secondary"
                                            className="w-100"
                                            size="sm"
                                            onClick={handleRefresh}
                                            disabled={refreshing}
                                        >
                                            {refreshing ? <Spinner size="sm" /> : 'Refresh'}
                                        </Button>
                                    </Col>
                                </Row>
                            </div>

                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        );
    };

    // Demographics Tab Content
    const DemographicsTab = ({ patient }) => (
        <Card className="border-0 shadow-sm">
            <Card.Header className="bg-light">
                <h5 className="mb-0">
                    <FaUser className="me-2" />
                    Patient Demographics
                </h5>
            </Card.Header>
            <Card.Body>
                <Row>
                    <Col md={6}>
                        <h6 className="section-title">Personal Information</h6>
                        <Table borderless size="sm">
                            <tbody>
                                <tr>
                                    <td><strong>Full Name:</strong></td>
                                    <td>{patient?.full_name || 'N/A'}</td>
                                </tr>
                                <tr>
                                    <td><strong>Date of Birth:</strong></td>
                                    <td>{patient?.date_of_birth ? new Date(patient.date_of_birth).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'N/A'}</td>
                                </tr>
                                <tr>
                                    <td><strong>Age:</strong></td>
                                    <td>{patient?.age || 'N/A'}</td>
                                </tr>
                                <tr>
                                    <td><strong>Gender:</strong></td>
                                    <td>{patient?.gender || 'N/A'}</td>
                                </tr>
                                <tr>
                                    <td><strong>Nationality:</strong></td>
                                    <td>{patient?.nationality || 'N/A'}</td>
                                </tr>
                            </tbody>
                        </Table>
                    </Col>
                    <Col md={6}>
                        <h6 className="section-title">Contact & Identification</h6>
                        <Table borderless size="sm">
                            <tbody>
                                <tr>
                                    <td><strong>Primary Phone:</strong></td>
                                    <td>{patient?.phone || 'N/A'}</td>
                                </tr>
                                <tr>
                                    <td><strong>Email:</strong></td>
                                    <td>{patient?.email || 'N/A'}</td>
                                </tr>
                                <tr>
                                    <td><strong>Emirates ID:</strong></td>
                                    <td>{patient?.emirates_id || 'N/A'}</td>
                                </tr>
                                <tr>
                                    <td><strong>MRN:</strong></td>
                                    <td>{patient?.mrn || 'N/A'}</td>
                                </tr>
                                <tr>
                                    <td><strong>Registration Status:</strong></td>
                                    <td>
                                        <Badge bg={patient?.is_registered ? 'success' : 'warning'}>
                                            {patient?.is_registered ? 'Completed' : 'Pending'}
                                        </Badge>
                                    </td>
                                </tr>
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );

    // Medical Records Tab Content - Updated with modern UI
    const MedicalRecordsTab = () => {
        const hasMedicalData = medicalHistory && (
            medicalHistory.blood_group ||
            medicalHistory.height ||
            medicalHistory.weight ||
            medicalHistory.bmi ||
            (medicalHistory.allergies && medicalHistory.allergies.length > 0) ||
            (medicalHistory.medical_conditions && medicalHistory.medical_conditions.length > 0) ||
            (medicalHistory.current_medications && medicalHistory.current_medications.length > 0) ||
            (medicalHistory.insurance_info && medicalHistory.insurance_info.length > 0)
        );

        return (
            <div className="medical-records-container">
                {/* Vital Signs Section */}
                <div className="medical-data-section">
                    <div className="medical-data-header">
                        <h3 className="medical-section-header">
                            Vital Signs & Measurements
                        </h3>
                    </div>

                    <div className="vital-signs-grid">
                        <div className="vital-sign-card">
                            <div className="vital-sign-header">
                                <div className="vital-sign-icon">
                                    <FaHeart />
                                </div>
                                <span className="vital-sign-label">Blood Group</span>
                            </div>
                            <div className="vital-sign-value">
                                {medicalHistory?.blood_group || 'N/A'}
                            </div>
                        </div>

                        <div className="vital-sign-card">
                            <div className="vital-sign-header">
                                <div className="vital-sign-icon">
                                    <FaUser />
                                </div>
                                <span className="vital-sign-label">Height</span>
                            </div>
                            <div className="vital-sign-value">
                                {medicalHistory?.height ? `${medicalHistory.height}` : 'N/A'}
                                <span className="vital-sign-unit">cm</span>
                            </div>
                        </div>

                        <div className="vital-sign-card">
                            <div className="vital-sign-header">
                                <div className="vital-sign-icon">
                                    <FaShieldAlt />
                                </div>
                                <span className="vital-sign-label">Weight</span>
                            </div>
                            <div className="vital-sign-value">
                                {medicalHistory?.weight ? `${medicalHistory.weight}` : 'N/A'}
                                <span className="vital-sign-unit">kg</span>
                            </div>
                        </div>

                        <div className="vital-sign-card">
                            <div className="vital-sign-header">
                                <div className="vital-sign-icon">
                                    <FaHistory />
                                </div>
                                <span className="vital-sign-label">BMI</span>
                            </div>
                            <div className="vital-sign-value">
                                {medicalHistory?.bmi ? medicalHistory.bmi.toFixed(1) : 'N/A'}
                            </div>
                        </div>

                        <div className="vital-sign-card">
                            <div className="vital-sign-header">
                                <div className="vital-sign-icon">
                                    <FaChartLine />
                                </div>
                                <span className="vital-sign-label">BMI Z-Score</span>
                            </div>
                            <div className="vital-sign-value">
                                {medicalHistory?.bmi_z_score !== null && medicalHistory?.bmi_z_score !== undefined
                                    ? medicalHistory.bmi_z_score.toFixed(2)
                                    : 'N/A'}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Medical Information Sections */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem' }}>
                    {/* Allergies Section */}
                    <div className="medical-data-section">
                        <div className="medical-data-header">
                            <h4 className="medical-data-title">
                                <FaShieldAlt className="medical-data-icon" />
                                Allergies
                            </h4>
                        </div>

                        {medicalHistory?.allergies && medicalHistory.allergies.length > 0 ? (
                            <ul className="medical-data-list">
                                {medicalHistory.allergies.map((allergy, index) => (
                                    <li key={index} className="medical-data-item">
                                        <span className="medical-data-label">
                                            {allergy.allergen || allergy.name || 'Unknown Allergen'}
                                        </span>
                                        <div className="medical-data-value">
                                            <span className="status-badge pending">Allergy</span>
                                            {allergy.severity && (
                                                <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                                                    {allergy.severity}
                                                </span>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className="empty-state">
                                <FaShieldAlt className="empty-state-icon" />
                                <p className="empty-state-text">No known allergies</p>
                            </div>
                        )}
                    </div>

                    {/* Medical Conditions Section */}
                    <div className="medical-data-section">
                        <div className="medical-data-header">
                            <h4 className="medical-data-title">
                                <FaHistory className="medical-data-icon" />
                                Medical Conditions
                            </h4>
                        </div>

                        {medicalHistory?.medical_conditions && medicalHistory.medical_conditions.length > 0 ? (
                            <ul className="medical-data-list">
                                {medicalHistory.medical_conditions.map((condition, index) => (
                                    <li key={index} className="medical-data-item">
                                        <span className="medical-data-label">
                                            {condition.condition || condition.name || 'Unknown Condition'}
                                        </span>
                                        <div className="medical-data-value">
                                            <span className="status-badge inactive">Condition</span>
                                            {condition.diagnosis_date && (
                                                <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                                                    {new Date(condition.diagnosis_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                                </span>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className="empty-state">
                                <FaHistory className="empty-state-icon" />
                                <p className="empty-state-text">No medical conditions recorded</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Insurance Information Section */}
                {medicalHistory?.insurance_info && medicalHistory.insurance_info.length > 0 && (
                    <div className="medical-data-section">
                        <div className="medical-data-header">
                            <h4 className="medical-data-title">
                                <FaIdCard className="medical-data-icon" />
                                Insurance Information
                            </h4>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
                            {medicalHistory.insurance_info.map((insurance, index) => (
                                <div key={index} className="vital-sign-card">
                                    <div className="vital-sign-header">
                                        <div className="vital-sign-icon">
                                            <FaIdCard />
                                        </div>
                                        <span className="vital-sign-label">
                                            {insurance.provider || insurance.insurance_provider || 'Insurance Provider'}
                                        </span>
                                    </div>

                                    <ul className="medical-data-list">
                                        <li className="medical-data-item">
                                            <span className="medical-data-label">Policy Number</span>
                                            <span className="medical-data-value">
                                                {insurance.policy_number || 'N/A'}
                                            </span>
                                        </li>
                                        <li className="medical-data-item">
                                            <span className="medical-data-label">Group Number</span>
                                            <span className="medical-data-value">
                                                {insurance.group_number || 'N/A'}
                                            </span>
                                        </li>
                                        {insurance.expiry_date && (
                                            <li className="medical-data-item">
                                                <span className="medical-data-label">Expires</span>
                                                <span className="medical-data-value">
                                                    {new Date(insurance.expiry_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                                </span>
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                {/* <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '2rem' }}>
                    <button className="medical-action-button primary">
                        <FaEdit />
                        Update Medical History
                    </button>
                    <button className="medical-action-button">
                        <FaFileAlt />
                        Generate Report
                    </button>
                </div> */}
            </div>
        );
    };

    // Medications Tab Content
    const MedicationsTab = () => (
        <Card className="border-0 shadow-sm">
            <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                    <FaPills className="me-2" />
                    Current Medications
                </h5>
                {!isParentView && (
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => setShowAddMedication(true)}
                    >
                        <FaPlus className="me-1" />
                        Add Medication
                    </Button>
                )}
            </Card.Header>
            <Card.Body>
                {medications.length > 0 ? (
                    <div className="row">
                        {medications.map((medication, index) => (
                            <div key={index} className="col-md-6 mb-3">
                                <Card className="medication-card h-100">
                                    <Card.Body className="p-3">
                                        <h6 className="medication-name text-primary">{medication.name}</h6>
                                        <p className="medication-dosage mb-2">
                                            <strong>Dosage:</strong> {medication.dosage}
                                        </p>
                                        <p className="medication-frequency mb-2">
                                            <strong>Frequency:</strong> {medication.frequency}
                                        </p>
                                        <p className="medication-route mb-2">
                                            <strong>Route:</strong> {medication.route}
                                        </p>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <Badge bg={medication.status === 'active' ? 'success' : 'secondary'}>
                                                {medication.status}
                                            </Badge>
                                            <small className="text-muted">
                                                Since: {medication.start_date ? new Date(medication.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'N/A'}
                                            </small>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-4">
                        <FaPills className="text-muted mb-3" size={48} />
                        <h6 className="text-muted">No medications found</h6>
                        <p className="text-muted">Current medications will appear here when prescribed.</p>
                    </div>
                )}
            </Card.Body>
        </Card>
    );

    // Documents Tab Content
    const DocumentsTab = () => (
        <Card className="border-0 shadow-sm">
            <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                    <FaFileAlt className="me-2" />
                    Patient Documents
                </h5>
                {!isParentView && (
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => setShowUploadDocument(true)}
                    >
                        <FaPlus className="me-1" />
                        Upload Document
                    </Button>
                )}
            </Card.Header>
            <Card.Body>
                {documents.length > 0 ? (
                    <div className="documents-list">
                        {documents.map((document, index) => (
                            <div key={index} className="document-item p-3 border rounded mb-3">
                                <div className="d-flex justify-content-between align-items-start">
                                    <div className="document-info">
                                        <h6 className="document-title mb-1">{document.title}</h6>
                                        <p className="document-description text-muted mb-2">
                                            {document.description || 'No description available'}
                                        </p>
                                        <div className="document-meta">
                                            <Badge bg="light" text="dark" className="me-2">
                                                {document.document_type}
                                            </Badge>
                                            <small className="text-muted">
                                                Uploaded: {document.uploaded_at ? new Date(document.uploaded_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'N/A'}
                                            </small>
                                        </div>
                                    </div>
                                    <div className="document-actions">
                                        <Button variant="outline-primary" size="sm" className="me-2">
                                            <FaEye className="me-1" />
                                            View
                                        </Button>
                                        <Button variant="outline-secondary" size="sm">
                                            <FaDownload className="me-1" />
                                            Download
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-4">
                        <FaFileAlt className="text-muted mb-3" size={48} />
                        <h6 className="text-muted">No documents found</h6>
                        <p className="text-muted">Patient documents will appear here when uploaded.</p>
                    </div>
                )}
            </Card.Body>
        </Card>
    );

    // Appointments Tab Content
    const AppointmentsTab = () => (
        <Card className="border-0 shadow-sm">
            <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                    <FaCalendarAlt className="me-2" />
                    Appointments & Scheduling
                </h5>
                <Button variant="outline-primary" size="sm">
                    <FaPlus className="me-1" />
                    Schedule Appointment
                </Button>
            </Card.Header>
            <Card.Body>
                {appointments.length > 0 ? (
                    <div className="appointments-list">
                        {appointments.map((appointment, index) => (
                            <div key={index} className="appointment-item p-3 border rounded mb-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="appointment-type">{appointment.type || 'Appointment'}</h6>
                                        <p className="appointment-datetime mb-1">
                                            <FaCalendarAlt className="me-2" />
                                            {appointment.scheduled_at ? new Date(appointment.scheduled_at).toLocaleString('en-GB', { hour12: true }) : 'Date TBD'}
                                        </p>
                                        <p className="appointment-provider text-muted mb-0">
                                            <FaUserMd className="me-2" />
                                            {appointment.provider || 'Provider TBD'}
                                        </p>
                                    </div>
                                    <Badge bg={appointment.status === 'confirmed' ? 'success' : 'warning'}>
                                        {appointment.status || 'Pending'}
                                    </Badge>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-4">
                        <FaCalendarAlt className="text-muted mb-3" size={48} />
                        <h6 className="text-muted">No appointments scheduled</h6>
                        <p className="text-muted">Upcoming appointments will appear here.</p>
                    </div>
                )}
            </Card.Body>
        </Card>
    );

    // Render loading state
    if (loading) {
        return (
            <>
                <Container fluid className="p-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Loading patient details...</p>
                        </div>
                    </div>
                </Container>
            </>
        );
    }

    // Render error state
    if (error) {
        return (
            <>
                <Container fluid className="p-4">
                    <Alert variant="danger">
                        <Alert.Heading>Error Loading Patient</Alert.Heading>
                        <p>{error}</p>
                        <Button variant="outline-danger" onClick={loadPatientData}>
                            Try Again
                        </Button>
                        <Button
                            variant="outline-secondary"
                            className="ms-2"
                            onClick={() => navigate('/patient-records')}
                        >
                            Back to Patient List
                        </Button>
                    </Alert>
                </Container>
            </>
        );
    }

    return (
        <>
            <Container fluid className="patient-detail-view p-4">
                {/* Patient Header */}
                <PatientHeader patient={patient} />

                {/* Tabbed Content */}
                <Tab.Container activeKey={activeTab} onSelect={handleTabChange}>
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom">
                            <Nav variant="tabs" className="border-0">
                                <Nav.Item>
                                    <Nav.Link eventKey="demographics">
                                        <FaUser className="me-2" />
                                        Demographics
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="medical">
                                        <FaHeart className="me-2" />
                                        Medical Records
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="clinical-forms">
                                        <FaClipboardList className="me-2" />
                                        Clinical Forms
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="medications">
                                        <FaPills className="me-2" />
                                        Medications
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="documents">
                                        <FaFileAlt className="me-2" />
                                        Documents
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="appointments">
                                        <FaCalendarAlt className="me-2" />
                                        Appointments
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="audit">
                                        <FaShieldAlt className="me-2" />
                                        Audit Trail
                                    </Nav.Link>
                                </Nav.Item>
                            </Nav>
                        </Card.Header>

                        <Tab.Content className="p-0">
                            <Tab.Pane eventKey="demographics">
                                <div className="p-4">
                                    <DemographicsTab patient={patient} />
                                </div>
                            </Tab.Pane>
                            <Tab.Pane eventKey="medical">
                                <div className="p-4">
                                    <MedicalRecordsTab />
                                </div>
                            </Tab.Pane>
                            <Tab.Pane eventKey="clinical-forms">
                                <div className="p-4">
                                    <PatientFormsHub patient={patient} viewMode={viewMode} isReadOnly={isParentView} />
                                </div>
                            </Tab.Pane>
                            <Tab.Pane eventKey="medications">
                                <div className="p-4">
                                    <PatientMedicationTab 
                                        patient={patient} 
                                        onMedicationOrdered={handleMedicationOrdered}
                                    />
                                </div>
                            </Tab.Pane>
                            <Tab.Pane eventKey="documents">
                                <div className="p-4">
                                    <DocumentsTab />
                                </div>
                            </Tab.Pane>
                            <Tab.Pane eventKey="appointments">
                                <div className="p-4">
                                    <AppointmentsTab />
                                </div>
                            </Tab.Pane>
                            <Tab.Pane eventKey="audit">
                                <div className="p-4">
                                    <Card className="border-0 shadow-sm">
                                        <Card.Header className="bg-light">
                                            <h5 className="mb-0">
                                                <FaShieldAlt className="me-2" />
                                                Access Audit Trail
                                            </h5>
                                        </Card.Header>
                                        <Card.Body>
                                            <div className="text-center py-4">
                                                <FaShieldAlt className="text-muted mb-3" size={48} />
                                                <h6 className="text-muted">Audit trail coming soon</h6>
                                                <p className="text-muted">Patient access logs will be displayed here.</p>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </div>
                            </Tab.Pane>
                        </Tab.Content>
                    </Card>
                </Tab.Container>
            </Container>

            {/* Action Modals - Only for nurses/staff, not for parents */}
            {!isParentView && (
                <>
                    {/* Discharge Modal */}
                    <DischargeModal
                        show={showDischargeModal}
                        onHide={() => setShowDischargeModal(false)}
                        onSubmit={handleDischarge}
                        patient={patient}
                        loading={dischargeLoading}
                    />

                    {/* Transfer Modal */}
                    <TransferModal
                        show={showTransferModal}
                        onHide={() => setShowTransferModal(false)}
                        onSubmit={handleTransfer}
                        patient={patient}
                        loading={transferLoading}
                    />

                    {/* Location Update Modal */}
                    <LocationModal
                        show={showLocationModal}
                        onHide={() => setShowLocationModal(false)}
                        onSubmit={handleLocationUpdate}
                        patient={patient}
                        loading={locationLoading}
                    />

                    {/* Edit Patient Modal */}
                    <EditPatientModal
                        show={showEditModal}
                        onHide={() => setShowEditModal(false)}
                        onSubmit={handleEditPatient}
                        patient={patient}
                        loading={editLoading}
                    />

                    {/* Staff Assignment Modal */}
                    <StaffAssignmentModal
                        show={showStaffAssignmentModal}
                        onHide={() => setShowStaffAssignmentModal(false)}
                        onSubmit={handleStaffAssignment}
                        patient={patient}
                        loading={staffAssignmentLoading}
                    />
                </>
            )}

        </>
    );
};

export default PatientDetailView; 